// Statutory-filings link-out — the official public page for a company's
// register record / filed documents, per market. Pure click-out (the platform
// scrapes nothing here); the Record panel's Vitals tab renders it as a
// "Statutory filings" row.
//
// Every market has an official lookup surface, but not every register supports
// a per-entity deep link on the id keyspace we store (see
// handover/reconciliation/Platform_RegistryId_Keyspace_List_2026-07-15.md).
// Three grades, encoded in `exact`:
//   • deep link on the stored registry id            → exact: true
//   • official register search, seeded where possible → exact: false
// Markets whose stored id is synthetic (HR name-keys, SI RIZDDZ) can only get
// the register's search page.

export interface FilingsLink {
  url: string;
  /** Register name shown as the link text ("Companies House", "CVR", …). */
  register: string;
  /** True = lands on the entity's own page; false = register search page. */
  exact: boolean;
}

const enc = encodeURIComponent;

export function statutoryFilingsLink(
  countryCode: string | null | undefined,
  registryId: string | null | undefined,
  legalName: string | null | undefined,
): FilingsLink | null {
  const id = registryId?.trim() ?? '';
  const name = legalName?.trim() ?? '';
  switch (countryCode) {
    case 'GB':
      return id
        ? {
            url: `https://find-and-update.company-information.service.gov.uk/company/${enc(id)}/filing-history`,
            register: 'Companies House',
            exact: true,
          }
        : null;
    case 'FR':
      return id
        ? {
            url: `https://annuaire-entreprises.data.gouv.fr/entreprise/${enc(id)}`,
            register: 'Annuaire des Entreprises',
            exact: true,
          }
        : null;
    case 'BE': {
      // Stored dotted (0784.377.533); KBO public wants bare digits.
      const digits = id.replace(/\D/g, '');
      return digits
        ? {
            url: `https://kbopub.economie.fgov.be/kbopub/toonondernemingps.html?ondernemingsnummer=${digits}`,
            register: 'KBO/BCE',
            exact: true,
          }
        : null;
    }
    case 'NO':
      return id
        ? {
            url: `https://virksomhet.brreg.no/nb/oppslag/enheter/${enc(id)}`,
            register: 'Brønnøysundregistrene',
            exact: true,
          }
        : null;
    case 'DK':
      return id
        ? {
            url: `https://datacvr.virk.dk/enhed/virksomhed/${enc(id)}`,
            register: 'CVR',
            exact: true,
          }
        : null;
    case 'EE':
      return id
        ? {
            url: `https://ariregister.rik.ee/eng/company/${enc(id)}`,
            register: 'Äriregister',
            exact: true,
          }
        : null;
    case 'FI':
      return id
        ? {
            url: `https://tietopalvelu.ytj.fi/yritys/${enc(id)}`,
            register: 'YTJ',
            exact: true,
          }
        : null;
    case 'NL':
      return id
        ? {
            url: `https://www.kvk.nl/zoeken/?source=all&q=${enc(id)}`,
            register: 'KVK Handelsregister',
            exact: false,
          }
        : null;
    case 'CZ':
      return id
        ? {
            url: `https://or.justice.cz/ias/ui/rejstrik-$firma?ico=${enc(id)}`,
            register: 'Obchodní rejstřík',
            exact: true,
          }
        : null;
    case 'SK':
      // registeruz.sk is the statutory financial-statements register itself.
      return id
        ? {
            url: `https://www.registeruz.sk/cruz-public/domain/accountingentity/simplesearch?query=${enc(id)}`,
            register: 'Register účtovných závierok',
            exact: true,
          }
        : null;
    case 'RO':
      return id
        ? {
            url: `https://mfinante.gov.ro/apps/infocodfiscal.html?cod=${enc(id)}`,
            register: 'ANAF / MFinanțe',
            exact: true,
          }
        : null;
    case 'LT':
      return id
        ? {
            url: `https://www.registrucentras.lt/jar/p/index.php?kod=${enc(id)}`,
            register: 'Registrų centras',
            exact: true,
          }
        : null;
    case 'CH':
      // Zefix resolves a UID typed into the name search.
      return id
        ? {
            url: `https://www.zefix.ch/en/search/entity/list?name=${enc(id)}`,
            register: 'Zefix',
            exact: true,
          }
        : null;
    case 'HU':
      // e-beszamolo is the statutory annual-report store; search by name
      // (the stored court-prefixed id isn't a query key there).
      return {
        url: 'https://e-beszamolo.im.gov.hu/oldal/beszamolo_kereses',
        register: 'e-beszámoló',
        exact: false,
      };
    case 'AT':
      return {
        url: 'https://justizonline.gv.at/jop/web/firmenbuchabfrage',
        register: 'Firmenbuch',
        exact: false,
      };
    case 'DE':
      return {
        url: name
          ? `https://www.unternehmensregister.de/ureg/result.html?submitaction=searchbutton&searchstring=${enc(name)}`
          : 'https://www.unternehmensregister.de/',
        register: 'Unternehmensregister',
        exact: false,
      };
    case 'BG':
      return {
        url: 'https://portal.registryagency.bg/CR/en/Reports/VerificationPersonOrg',
        register: 'Търговски регистър',
        exact: false,
      };
    case 'ES':
      return {
        url: 'https://www.registradores.org/el-colegio/registro-mercantil',
        register: 'Registro Mercantil',
        exact: false,
      };
    case 'GR':
      return {
        url: 'https://publicity.businessportal.gr/',
        register: 'ΓΕΜΗ',
        exact: false,
      };
    case 'HR':
      return {
        url: 'https://sudreg.pravosudje.hr/registar/f?p=150:1',
        register: 'Sudski registar',
        exact: false,
      };
    case 'IE':
      return {
        url: 'https://core.cro.ie/search',
        register: 'CRO',
        exact: false,
      };
    case 'IT':
      return {
        url: 'https://www.registroimprese.it/',
        register: 'Registro Imprese',
        exact: false,
      };
    case 'LV':
      return {
        url: 'https://info.ur.gov.lv/#/data-search',
        register: 'Uzņēmumu reģistrs',
        exact: false,
      };
    case 'PL':
      return {
        url: 'https://wyszukiwarka-krs.ms.gov.pl/',
        register: 'KRS',
        exact: false,
      };
    case 'PT':
      return {
        url: 'https://publicacoes.mj.pt/Pesquisa.aspx',
        register: 'Publicações MJ',
        exact: false,
      };
    case 'SE':
      return {
        url: 'https://foretagsinfo.bolagsverket.se/sok-foretagsinformation-web/foretag',
        register: 'Bolagsverket',
        exact: false,
      };
    case 'SI':
      return {
        url: 'https://www.ajpes.si/prs/',
        register: 'AJPES',
        exact: false,
      };
    default:
      return null;
  }
}
